package com.devi3east.bashbuddy.dashboard.ui

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.devi3east.bashbuddy.auth.domain.models.UserType
import com.devi3east.bashbuddy.dashboard.ui.components.DashboardPurchaseCard
import com.devi3east.bashbuddy.dashboard.ui.components.EmptyDashboardContent
import com.devi3east.bashbuddy.dashboard.ui.components.OrganizerDashboardCard
import com.devi3east.bashbuddy.dashboard.ui.components.dialogs.AdjustTicketDialog
import com.devi3east.bashbuddy.dashboard.ui.components.dialogs.ConfirmDeleteDialog
import com.devi3east.bashbuddy.dashboard.ui.components.dialogs.EditEventDialog
import com.devi3east.bashbuddy.dashboard.ui.components.dialogs.InfoAlertDialog
import com.devi3east.bashbuddy.dashboard.ui.components.dialogs.UpdateTicketDialog
import com.devi3east.bashbuddy.features.app.data.AppState
import com.devi3east.bashbuddy.features.app.data.Screen
import com.devi3east.bashbuddy.features.events.common.data.models.Event
import com.devi3east.bashbuddy.features.events.common.data.models.Ticket
import com.devi3east.bashbuddy.features.events.requests.CreateDraftTicketRequest
import com.devi3east.bashbuddy.features.events.requests.UpdateDraftEventRequest
import com.devi3east.bashbuddy.features.events.requests.UpdateTicketRequest
import com.devi3east.bashbuddy.account.organization.domain.models.Organization
import com.devi3east.bashbuddy.prizeList.domain.models.PrizeType
import com.devi3east.bashbuddy.prizeList.requests.CreatePrizeRequest
import com.devi3east.bashbuddy.prizeList.requests.UpdatePrizeRequest
import com.devi3east.bashbuddy.account.profile.domain.models.Profile
import com.devi3east.bashbuddy.purchase.domain.models.PurchaseStatus
import com.devi3east.bashbuddy.purchase.domain.models.PurchaseUiModel

@Composable
fun DashboardRoute(
    appState: AppState,
    profile: Profile,
    organization: Organization?,
    navigateToEventDetail: (String, String) -> Unit,
    navigateToTicketDetail: (String) -> Unit,
    navigateToPrizeList: (String, String) -> Unit
) {
    val viewModel = appState.dashboardViewModel
    val uiState by viewModel.uiState.collectAsState()

    LaunchedEffect(true) {
        viewModel.loadDashboardData()
    }

    DashboardScreen(
        appState = appState,
        profile = profile,
        organization = organization,
        events = uiState.eventList,
        tickets = uiState.ticketList,
        purchases = uiState.purchaseList,
        uiState = uiState,
        onCreateEvent = viewModel::createDraftEventAndOpenEdit,
        onCreateTicket = viewModel::createDraftTicketAndOpenEdit,
        onUpdateEvent = viewModel::updateEventAndRefresh,
        onUpdateTicket = viewModel::updateTicketAndRefresh,
        onEditEventDialog = viewModel::openEditEventDialog,
        onUpdateTicketDialog = viewModel::openUpdateTicketDialog,
        onConfirmDeleteTicketDialog = viewModel::confirmDeleteTicket,
        onConfirmDeleteEventDialog = viewModel::confirmDeleteEvent,
        onDeleteTicket = viewModel::deleteTicketAndRefresh,
        onDeleteEvent = viewModel::deleteEventAndRefresh,
        onClearDeleteMessage = viewModel::clearDeleteMessage,
        onDismissDeleteDialog = viewModel::dismissDeleteDialog,
        onDismissUpdateTicketDialog = viewModel::dismissEditTicketDialog,
        onDismissEditEventDialog = viewModel::dismissEditEventDialog,
        onAdjustTicket = viewModel::openAdjustDialog,
        onDismissAdjustDialog = viewModel::dismissAdjustDialog,
        onSubmitAdjust = viewModel::submitAdjustAndRefresh,
        onPublishEvent = viewModel::publishEventAndRefresh,
        navigateToEventDetail = navigateToEventDetail,
        onBrowseEvents = { appState.navController.navigate(Screen.EventsScreen.route) },
        onTogglePauseTicket = { ticketId, pause -> viewModel.togglePauseTicket(ticketId, pause) },
        navigateToTicketDetail = navigateToTicketDetail,
        navigateToPrizeList = navigateToPrizeList,
        onTicketInfoClick = { appState.navController.navigate(Screen.InformationScreen.route) },
        onEventImageUploaded = viewModel::onEventImageUploaded,
        onTicketImageUploaded = viewModel::onTicketImageUploaded,
        onAddSideRaffle = { eventId, req -> viewModel.createSizeRaffleFromTicket(eventId, req) },
        onUpdateSideRaffle = { prizeId, req -> viewModel.updatePrize(prizeId, req) },
        onConfirmFirstEventCreatedDialog = { updatedOrganization ->
            appState.authViewModel.updateOrganizationInfo(updatedOrganization)
        }
    )
}

@Composable
fun DashboardScreen(
    appState: AppState,
    profile: Profile,
    organization: Organization?,
    events: List<Event>,
    tickets: List<Ticket>,
    purchases: List<PurchaseUiModel>,
    uiState: DashboardUiState,
    onCreateEvent: () -> Unit,
    onUpdateEvent: (String, UpdateDraftEventRequest) -> Unit,
    onPublishEvent: (String) -> Unit,
    onCreateTicket: (String, CreateDraftTicketRequest) -> Unit,
    onUpdateTicket: (String, UpdateTicketRequest) -> Unit,
    onEditEventDialog: (String) -> Unit,
    onUpdateTicketDialog: (String) -> Unit,
    onConfirmDeleteTicketDialog: (String) -> Unit,
    onConfirmDeleteEventDialog: (String) -> Unit,
    onDeleteTicket: (String) -> Unit,
    onDeleteEvent: (String) -> Unit,
    onClearDeleteMessage: () -> Unit,
    onDismissDeleteDialog: () -> Unit,
    onDismissUpdateTicketDialog: () -> Unit,
    onDismissEditEventDialog: () -> Unit,
    onAdjustTicket: (String) -> Unit,
    onDismissAdjustDialog: () -> Unit,
    onSubmitAdjust: (String, Int) -> Unit,
    navigateToEventDetail: (String, String) -> Unit,
    onBrowseEvents: () -> Unit,
    onTogglePauseTicket: (String, Boolean) -> Unit,
    navigateToTicketDetail : (String) -> Unit,
    navigateToPrizeList : (String, String) -> Unit,
    onTicketInfoClick: () -> Unit,
    onEventImageUploaded: (String) -> Unit,
    onTicketImageUploaded: (String, String) -> Unit,
    onAddSideRaffle: (String, CreatePrizeRequest) -> Unit,
    onUpdateSideRaffle: (String, UpdatePrizeRequest) -> Unit,
    onConfirmFirstEventCreatedDialog: (Organization) -> Unit
) {

    var showFirstEventCreatedDialog by remember { mutableStateOf(false) }

    val eventToEdit = uiState.eventList.find { it._id == uiState.eventToEditId }
    val ticketToUpdate = uiState.ticketList.find { it._id == uiState.ticketToUpdateId }
    val ticketToAdjust = uiState.ticketList.find { it._id == uiState.ticketToAdjustId }
    val sideRafflePrizeId: String? = ticketToUpdate?.let { ticket ->
        uiState.prizeList
            .find { it.ticketId == ticket._id && it.prizeType == PrizeType.SIDE_RAFFLE }
            ?._id
    }

    val expandedEventIds = remember { mutableStateMapOf<String, Boolean>() }
    val previousEventIds = remember { mutableStateListOf<String>() }

    var showStripeRestrictionDialog by remember { mutableStateOf(false) }
    var isEditing by remember { mutableStateOf(false) }

    LaunchedEffect(events) {
        val currentIds = events.map { it._id }
        val newIds = currentIds - previousEventIds.toSet()
        previousEventIds.clear()
        previousEventIds.addAll(currentIds)
        newIds.firstOrNull()?.let { newId -> expandedEventIds[newId] = true }
    }

    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val maxWidthDp = maxWidth
        val targetWidth = when {
            maxWidthDp < 600.dp -> maxWidthDp
            maxWidthDp < 900.dp -> 600.dp
            else -> 720.dp
        }

        val contentModifier = Modifier
            .widthIn(max = targetWidth)
            .align(Alignment.TopCenter)

        Box(modifier = Modifier.fillMaxSize()) {
            when {
                uiState.isDashboardLoading -> Box(Modifier.fillMaxSize(), Alignment.Center) {
                    CircularProgressIndicator()
                }
                uiState.errorMessage != null -> Box(Modifier.fillMaxSize(), Alignment.Center) {
                    Text(text = uiState.errorMessage, color = MaterialTheme.colorScheme.error)
                }
                profile.userType == UserType.ORGANIZATION && uiState.eventList.isEmpty() -> {
                    EmptyDashboardContent(
                        userType = UserType.ORGANIZATION,
                        onBrowseEvents = { },
                        onCreateEvent = onCreateEvent
                    )
                }
                profile.userType == UserType.USER && uiState.purchaseList.isEmpty() -> {
                    EmptyDashboardContent(
                        userType = UserType.USER,
                        onBrowseEvents = onBrowseEvents,
                        onCreateEvent = { }
                    )
                }
                else -> LazyColumn(
                    modifier = contentModifier.padding(horizontal = 16.dp)
                ) {
                    if (profile.userType == UserType.ORGANIZATION) {
                        items(events) { event ->
                            val isExpanded = expandedEventIds[event._id] ?: false
                            val eventTickets = tickets.filter { it.eventId == event._id }
                            val eventPending = uiState.pendingAssignmentsPerEvent[event._id] ?: 0

                            OrganizerDashboardCard(
                                event = event,
                                eventTickets = eventTickets,
                                isExpanded = isExpanded,
                                eventPending = eventPending,
                                onExpandToggle = { id -> expandedEventIds[id] = !isExpanded },
                                onEditEvent = onEditEventDialog,
                                onDeleteEvent = onConfirmDeleteEventDialog,
                                onNavigateToDetail = navigateToEventDetail,
                                onCreateTicket = { eventId, req ->
                                    if (organization?.stripeAccount?.onboardingComplete == true) {
                                        isEditing = false
                                        onCreateTicket(eventId, req)
                                    } else showStripeRestrictionDialog = true
                                },
                                onEditTicket = { ticket -> isEditing = true; onUpdateTicketDialog(ticket) },
                                onPublishEvent = { eventId -> onPublishEvent(eventId) },
                                onDeleteTicket = onConfirmDeleteTicketDialog,
                                onAdjustTicket = onAdjustTicket,
                                onTogglePauseTicket = onTogglePauseTicket,
                                pendingAssignmentsPerTicket = uiState.pendingAssignmentsPerTicket,
                                navigateToTicketDetail = navigateToTicketDetail,
                                navigateToPrizeList = navigateToPrizeList
                            )
                        }
                    }

                    if (profile.userType == UserType.USER) {
                        val paidItems = purchases
                            .filter { it.status == PurchaseStatus.PAID }
                            .flatMap { it.items }

                        val groupedByEvent = paidItems.groupBy { it.eventId }

                        groupedByEvent.forEach { (_, itemsForEvent) ->
                            item {
                                DashboardPurchaseCard(
                                    items = itemsForEvent,
                                    navigateToEventDetail = navigateToEventDetail
                                )
                            }
                        }
                    }
                }
            }

            // Stripe restriction dialog
            if (showStripeRestrictionDialog) {
                InfoAlertDialog(
                    title = "Stripe Setup Required",
                    message = "You need to finish connecting your Stripe account before adding tickets. Visit the Organization tab below to complete onboarding.",
                    onDismiss = { showStripeRestrictionDialog = false }
                )
            }

            /*if (showStripeRestrictionDialog) {
                InfoAlertDialog(
                    title = "Ticket Sales Temporarily Unavailable",
                    message = "Online ticket sales are currently unavailable while we complete improvements to our payment system. We're actively working on restoring this feature and will notify organizations as soon as ticket sales are available again. Thank you for your patience.",
                    onDismiss = { showStripeRestrictionDialog = false }
                )
            }*/

            // Delete / Edit / Adjust dialogs remain the same
            if (uiState.isDeleteTicketConfirmDialogVisible && uiState.ticketToDeleteId != null) {
                ConfirmDeleteDialog(
                    title = "Delete Ticket?",
                    message = uiState.deleteMessage.takeUnless { it.isNullOrBlank() }
                        ?: "Are you sure you want to delete this ticket? Any associated side raffles will also be deleted.",
                    onConfirm = { onDeleteTicket(uiState.ticketToDeleteId) },
                    onDismiss = {
                        onDismissDeleteDialog()
                        onClearDeleteMessage()
                    }
                )
            }

            if (uiState.isDeleteEventConfirmDialogVisible && uiState.eventToDeleteId != null) {
                ConfirmDeleteDialog(
                    title = "Delete Event?",
                    message = uiState.deleteMessage.takeUnless { it.isNullOrBlank() }
                        ?: "Are you sure you want to delete this event?",
                    onConfirm = { onDeleteEvent(uiState.eventToDeleteId) },
                    onDismiss = {
                        onDismissDeleteDialog()
                        onClearDeleteMessage()
                    }
                )
            }

            if (uiState.isUpdateTicketDialogVisible && ticketToUpdate != null) {
                UpdateTicketDialog(
                    token = appState.token!!,
                    ticket = ticketToUpdate,
                    prizeId = sideRafflePrizeId,
                    onDismiss = {
                        isEditing = false
                        onDismissUpdateTicketDialog()
                    },
                    onSubmit = {
                        onUpdateTicket(ticketToUpdate._id, it)
                        isEditing = false
                        onDismissUpdateTicketDialog()
                    },
                    onTicketInfoClick = onTicketInfoClick,
                    onTicketImageUploaded = onTicketImageUploaded,
                    onAddSideRaffle = onAddSideRaffle,
                    onUpdateSideRaffle = onUpdateSideRaffle
                )
            }

            if (uiState.isEditEventDialogVisible && eventToEdit != null) {
                EditEventDialog(
                    token = appState.token!!,
                    event = eventToEdit,
                    onDismiss = onDismissEditEventDialog,
                    onSubmit = {
                        onUpdateEvent(eventToEdit._id, it)
                        onDismissEditEventDialog()

                        if (
                            profile.userType == UserType.ORGANIZATION &&
                            organization?.showFirstEventCreatedDialog == true
                        ) {
                            showFirstEventCreatedDialog = true
                        }
                    },
                    onEventImageUploaded = onEventImageUploaded
                )
            }

            if (uiState.isAdjustDialogVisible && ticketToAdjust != null) {
                AdjustTicketDialog(
                    ticketTitle = ticketToAdjust.title ?: "Untitled Ticket",
                    maxQuantity = ticketToAdjust.maxQuantity ?: 0,
                    availableQuantity = ticketToAdjust.availableQuantity ?: 0,
                    ticketsSold = ticketToAdjust.ticketsSold,
                    onDismiss = onDismissAdjustDialog,
                    onSubmit = { newAvailable ->
                        onSubmitAdjust(ticketToAdjust._id, newAvailable)
                        onDismissAdjustDialog()
                    }
                )
            }

            if (profile.userType == UserType.ORGANIZATION && events.isNotEmpty()) {
                FloatingActionButton(
                    onClick = { onCreateEvent() },
                    modifier = Modifier
                        .align(Alignment.BottomStart)
                        .padding(start = 10.dp, bottom = 10.dp)
                ) {
                    Icon(Icons.Default.Add, contentDescription = "Create Event")
                }
            }

            if (
                organization != null &&
                showFirstEventCreatedDialog &&
                profile.userType == UserType.ORGANIZATION
            ) {
                InfoAlertDialog(
                    title = "Your event is ready—publish when you’re ready!",
                    message = "Your event is currently saved as a draft and hidden from the public. This gives you time to update information or add event tickets before launch. When everything looks right, click Publish to add it to the event feed. You can keep editing and adding tickets at any time, even after it has been published.",
                    onDismiss = {
                        showFirstEventCreatedDialog = false
                        onConfirmFirstEventCreatedDialog(
                            organization.copy(showFirstEventCreatedDialog = false)
                        )
                    },
                    confirmText = "OK"
                )
            }
        }
    }
}